import { useState, useEffect, useCallback } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

/**
 * Categorías de incidencia — lectura pública (chips del mapa, wizard de
 * reporte) más CRUD de administración (HU-005 Fase 4, exclusivo de personal
 * `ADMINISTRADOR`/`SUPERADMIN`, gateado por `RutaProtegidaPanel` en el panel,
 * no acá). `crearCategoria`/`actualizarCategoria` mandan `multipart/form-data`
 * cuando hay un logo nuevo (`cat_fileLogo` es un `File`), o JSON si no.
 */
const useCategorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = (extra = {}) => {
    const token = tokenManager.getAccessToken();
    return token && !tokenManager.isTokenExpired()
      ? { Authorization: `Bearer ${token}`, ...extra }
      : extra;
  };

  const manejarRespuesta = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const erroresDeCampo = Object.entries(data)
        .filter(([campo]) => campo !== 'error' && campo !== 'detail')
        .map(([campo, mensajes]) => `${campo}: ${Array.isArray(mensajes) ? mensajes.join(', ') : mensajes}`)
        .join(' — ');
      throw new Error(data.error || data.detail || erroresDeCampo || 'Error inesperado del servidor');
    }
    return data;
  };

  // Arma el body como FormData (si hay un logo nuevo que subir) o JSON —
  // `cat_fileLogo` llega como `File` desde el formulario del panel solo
  // cuando el administrador eligió uno nuevo.
  const construirBody = (datos) => {
    const tieneLogoNuevo = datos.cat_fileLogo instanceof File;
    if (!tieneLogoNuevo) {
      const { cat_fileLogo, ...resto } = datos;
      return { body: JSON.stringify(resto), headers: { 'Content-Type': 'application/json' } };
    }
    const formData = new FormData();
    Object.entries(datos).forEach(([campo, valor]) => {
      if (valor !== null && valor !== undefined) formData.append(campo, valor);
    });
    return { body: formData, headers: {} };
  };

  const cargarCategorias = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.categoria.listar, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      const lista = Array.isArray(data) ? data : data.categorias || [];
      // Orden por defecto (HU-023): primero la prioridad manual
      // (`cat_inOrden`, menor = primero), y a igual prioridad, alfabético.
      const ordenada = [...lista].sort((a, b) => {
        const oa = Number(a.cat_inOrden) || 0;
        const ob = Number(b.cat_inOrden) || 0;
        if (oa !== ob) return oa - ob;
        return (a.cat_chNombre || '').localeCompare(b.cat_chNombre || '', 'es');
      });
      setCategorias(ordenada);
      return { success: true, data: ordenada };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const crearCategoria = async (datos) => {
    setLoading(true);
    setError(null);
    try {
      const { body, headers } = construirBody(datos);
      const response = await fetch(API_CIFRA.categoria.crear, {
        method: 'POST',
        headers: authHeaders(headers),
        body,
      });
      const categoria = await manejarRespuesta(response);
      return { success: true, categoria };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const actualizarCategoria = async (id, datos) => {
    setLoading(true);
    setError(null);
    try {
      const { body, headers } = construirBody(datos);
      const response = await fetch(API_CIFRA.categoria.actualizar(id), {
        method: 'PUT',
        headers: authHeaders(headers),
        body,
      });
      const categoria = await manejarRespuesta(response);
      return { success: true, categoria };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const eliminarCategoria = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.categoria.eliminar(id), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) await manejarRespuesta(response);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const obtenerCategoriaPorId = (id) => categorias.find((cat) => cat.cat_inId === id);

  useEffect(() => { cargarCategorias(); }, [cargarCategorias]);

  return {
    categorias,
    loading,
    error,
    cargarCategorias,
    crearCategoria,
    actualizarCategoria,
    eliminarCategoria,
    obtenerCategoriaPorId,
  };
};

export default useCategorias;
